import { useCallback } from 'react';

import { PaymentMethodCode, type PaymentOption, type useProcessPayment } from '@src/features/payments';
import type { PaymentErrorDialogCopy } from '@src/features/payments/errors';

type UsePayWithPixInput = {
  dialog: PaymentErrorDialogCopy | null;
  dismiss: () => void;
  options: PaymentOption[];
  pay: ReturnType<typeof useProcessPayment>['pay'];
  select: (option: PaymentOption) => void;
  onGenerated: () => void;
};

// The second button of the error dialog. Whether pix is on offer at all is the dialog's call —
// this only carries it out: leave the dialog, move the checkout to pix and ask for the code.
export const usePayWithPix = ({ dialog, dismiss, options, pay, select, onGenerated }: UsePayWithPixInput) =>
  useCallback(async () => {
    dismiss();

    if (!dialog?.canPayWithPix) {
      return;
    }

    const pixOption = options.find((option) => option.paymentMethod.code === PaymentMethodCode.Pix);

    if (!pixOption) {
      return;
    }

    select(pixOption);

    const result = await pay(pixOption.paymentMethod.id);

    if (result?.success) {
      onGenerated();
    }
  }, [dialog?.canPayWithPix, dismiss, onGenerated, options, pay, select]);

export default usePayWithPix;
